import { inject } from '@angular/core';
import { Observable, defer, filter, of, share, startWith, switchMap, take, tap } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { AccessTokenProvider } from '../access-token.provider';

import { AppServiceIntegrationService } from './app-service-integration.service';
import { isValidToken } from './token-util';

const ACCESS_TOKEN_STORAGE_KEY = 'mobi.access_token';

export class AppServiceAuthService implements AccessTokenProvider {
    #logger = inject(Logger).createCustomLogger('OAUTH');
    #appServiceIntegrationService = inject(AppServiceIntegrationService);
    #storage = window.localStorage;

    #fetchAndStoreAccessToken$ = defer(() => this.#appServiceIntegrationService.fetchNewAccessToken()).pipe(
        tap((token: string) => this.#storage.setItem(ACCESS_TOKEN_STORAGE_KEY, token)),
        share()
    );

    constructor() {
        this.#logger.debug('Use App Service build-in authentication.');
        this.#appServiceIntegrationService.init();
    }

    readonly accessTokenOnce$: Observable<string> = defer(() =>
        of(this.#storage.getItem(ACCESS_TOKEN_STORAGE_KEY))
    ).pipe(
        switchMap((accessToken: string | null) =>
            isValidToken(accessToken) ? of(accessToken) : this.#fetchAndStoreAccessToken$
        ),
        take(1)
    );

    readonly accessTokenChanged$: Observable<string> = this.#appServiceIntegrationService.accessTokenChanged$.pipe(
        startWith(this.#storage.getItem(ACCESS_TOKEN_STORAGE_KEY)),
        filter(Boolean),
        tap(() => this.#logger.debug('Access token changed'))
    );
}
